import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLeadModalPrefill } from "../shared/useLeadModalPrefill.js";
import { setAuthSessionCookies } from "@/lib/authCookies";
import {
  mapTrialSignupError,
  registerTrialAccount,
  TRIAL_DUPLICATE_EMAIL_MESSAGE,
} from "@/lib/landingSignup";
import { trackRegistrationIfAuthenticated } from "@/lib/leadEvents";
import { LEAD_CAPTURE_AI_EMPLOYEE_OPTIONS } from "./leadCaptureEmployeeOptions.js";
import { CTA_MICRO_DISCLOSURE } from "../shared/ctaContent.js";

const AI_EMPLOYEE_OPTIONS = LEAD_CAPTURE_AI_EMPLOYEE_OPTIONS;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const LeadCaptureModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState("");
  const [aiEmployee, setAiEmployee] = useState(AI_EMPLOYEE_OPTIONS[0].value);
  const [status, setStatus] = useState("form");
  const [errorMessage, setErrorMessage] = useState("");
  const [registeredEmail, setRegisteredEmail] = useState("");
  const { emailInputRef, initialBotType } = useLeadModalPrefill(isOpen, setEmail);

  useEffect(() => {
    if (initialBotType && AI_EMPLOYEE_OPTIONS.some((o) => o.value === initialBotType)) {
      setAiEmployee(initialBotType);
    }
  }, [initialBotType]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape" && status !== "loading") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEsc);
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleEsc);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose, status]);

  useEffect(() => {
    if (!isOpen) {
      setStatus("form");
      setErrorMessage("");
      setRegisteredEmail("");
    }
  }, [isOpen]);

  const handleClose = () => {
    if (status === "loading") return;
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim();

    if (!EMAIL_RE.test(normalizedEmail)) {
      setErrorMessage("Please enter a valid email address.");
      return;
    }

    setErrorMessage("");
    setStatus("loading");

    try {
      const data = await registerTrialAccount({
        email: normalizedEmail,
        botType: aiEmployee,
      });

      if (data.status === "ok") {
        const token = data.token;
        const botType = data.bot_type || aiEmployee;
        const emailLower = normalizedEmail.toLowerCase();

        setAuthSessionCookies({
          token,
          email: emailLower,
          accountType: data.account_type || "client",
          botType,
        });

        trackRegistrationIfAuthenticated().catch((err) =>
          console.warn("[leadEvents] is_registered failed", err),
        );

        setRegisteredEmail(emailLower);
        setStatus("success");
      } else if (data.status === "error") {
        if (data.message === "The user already exists") {
          setErrorMessage(TRIAL_DUPLICATE_EMAIL_MESSAGE);
        } else {
          setErrorMessage(data.message || "An error occurred. Please try again.");
        }
        setStatus("form");
      } else {
        setErrorMessage("Unexpected response from the server. Please try again.");
        setStatus("form");
      }
    } catch (err) {
      console.error("Trial registration error:", err);
      setErrorMessage(mapTrialSignupError(err));
      setStatus("form");
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="quiet-modal-overlay"
      onClick={handleClose}
      data-testid="lead-modal-overlay"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="lead-modal-title"
        className="quiet-modal-card"
        onClick={(event) => event.stopPropagation()}
        data-testid="lead-modal"
      >
        <button
          type="button"
          onClick={handleClose}
          className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-main)] transition-colors p-1"
          aria-label="Close dialog"
          disabled={status === "loading"}
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {status === "success" ? (
          <div className="text-center py-4" data-testid="lead-modal-success">
            <h2 id="lead-modal-title" className="text-xl font-bold text-[var(--text-main)] mb-3">
              You&apos;re all set
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-6 leading-relaxed">
              We sent login details to {registeredEmail}. Check your inbox for your password.
            </p>
            <Link
              to="/dashboard"
              className="quiet-cta quiet-cta-primary w-full inline-flex items-center justify-center"
              onClick={handleClose}
              data-testid="lead-modal-open-dashboard"
            >
              [ Open Dashboard ↗ ]
            </Link>
          </div>
        ) : (
          <>
            <h2 id="lead-modal-title" className="text-xl font-bold text-[var(--text-main)] pr-8 mb-1">
              Drop your email.
            </h2>
            <p className="text-sm text-[var(--text-muted)] mb-5 leading-relaxed">
              You&apos;ll land in the Dashboard to manage your AI employee right away.
            </p>

            <form onSubmit={handleSubmit} className="quiet-modal-form space-y-4" data-testid="lead-modal-form">
              <div className="quiet-field">
                <label htmlFor="lead-ai-employee" className="quiet-field-label">
                  Select Your AI Employee
                </label>
                <div className="quiet-select-wrap">
                  <select
                    id="lead-ai-employee"
                    name="aiEmployee"
                    value={aiEmployee}
                    onChange={(event) => setAiEmployee(event.target.value)}
                    className="quiet-select"
                    disabled={status === "loading"}
                  >
                    {AI_EMPLOYEE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="quiet-field">
                <label htmlFor="lead-email" className="sr-only">
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="lead-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  className="quiet-input"
                  placeholder="you@email.com"
                  disabled={status === "loading"}
                />
              </div>

              {errorMessage && (
                <p className="text-sm text-red-400" role="alert">
                  {errorMessage}
                </p>
              )}

              <button
                type="submit"
                className="quiet-cta quiet-cta-primary w-full"
                disabled={status === "loading"}
                data-testid="lead-modal-submit"
              >
                {status === "loading" ? "Loading..." : "[ Open Dashboard ↗ ]"}
              </button>
              <div className="cursor-cta-caption-block text-center" data-testid="lead-modal-cta-disclosure">
                <p className="cursor-cta-caption">{CTA_MICRO_DISCLOSURE.line1}</p>
                <p className="cursor-cta-caption cursor-cta-caption-secondary">
                  {CTA_MICRO_DISCLOSURE.line2}
                </p>
              </div>
              <p className="quiet-modal-legal">
                By clicking &quot;Open Dashboard&quot;, you agree to our{" "}
                <Link to="/terms" className="quiet-modal-legal-link">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy-policy" className="quiet-modal-legal-link">
                  Privacy Policy
                </Link>
                .
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default LeadCaptureModal;
